import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  IosShare as ShareIcon,
  AutoAwesomeRounded as SparkleIcon,
  PlayCircleOutlineRounded as PlayIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import {
  DISTINCTIVE_TEMPLATES,
  DistinctiveField,
  DistinctiveTemplate,
  fillDistinctiveTemplate,
} from '../../distinctive/distinctiveTemplates';
import { api } from '../../services/api';
import SuperConnectorInterestDialog, { ConnectorInterestUser } from './SuperConnectorInterestDialog';

export type DistinctiveValues = Record<DistinctiveField, string>;

interface DistinctiveDialogProps {
  open: boolean;
  onClose: () => void;
  /** Field values injected into each design's {{ placeholders }} (agent/agency lines). */
  values: DistinctiveValues;
  /**
   * Current agent identity — used to prefill the Super Connector Register
   * Interest form (name/CEA locked, mobile/email editable).
   */
  user?: ConnectorInterestUser;
  /** Optional subset used by focused flows such as the Super Connector demo. */
  templates?: DistinctiveTemplate[];
  /** Dialog heading and supporting copy. */
  title?: string;
  description?: string;
  /**
   * Called after a design is successfully shared or downloaded (i.e. a share was
   * recorded server-side). Lets the parent refresh Price Promo access, which
   * sharing unlocks.
   */
  onShared?: () => void;
  /** Called after the Super Connector interest form is submitted successfully. */
  onRegistered?: () => void;
  /** Hide the Register Interest / View Demo actions (for unlock prompts unrelated to Super Connector). */
  hideInterestActions?: boolean;
}

/** Wait for every <img> under el to finish loading (resolves even on error). */
function waitForImages(el: HTMLElement): Promise<void> {
  const imgs = Array.from(el.querySelectorAll('img'));
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
}

/**
 * iOS Safari often jetsams the tab on a 2× html2canvas + base64 data URL.
 * Prefer scale 1 on coarse-pointer / iOS — same approach as Price Promo share.
 */
function captureScale(): number {
  if (typeof window === 'undefined') return 1;
  const ua = navigator.userAgent || '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const coarse = window.matchMedia?.('(pointer: coarse)').matches;
  return isIOS || coarse ? 1 : 2;
}

function canvasToObjectUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('canvas.toBlob returned null'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      'image/png',
    );
  });
}

const GRID_GAP = 16; // theme.spacing(2)

/**
 * "Distinctives" — a gallery of shareable EDMs. Tapping a design renders it at
 * full size off-screen, rasterises it with html2canvas, and offers the result
 * for download / native share (same flow as the Price Promo share tab, but
 * with per-template canvas dimensions since EDMs are portrait).
 */
const DistinctiveDialog: React.FC<DistinctiveDialogProps> = ({
  open,
  onClose,
  values,
  user,
  templates = DISTINCTIVE_TEMPLATES,
  title = 'Distinctives',
  description = 'Tap a design to generate a shareable EDM with your details.',
  onShared,
  onRegistered,
  hideInterestActions = false,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Register Interest gating: enabled once the agent has shared the Super
  // Connector design (recorded as "Super Connector Register Interest").
  const [interestEligible, setInterestEligible] = useState(false);
  const [interestOpen, setInterestOpen] = useState(false);
  // Width of one gallery cell, measured so the fixed-size
  // template markup can be transform-scaled to fit each preview.
  const [cellWidth, setCellWidth] = useState(0);

  // Callback-ref state (not a plain ref): the MUI Dialog portals its children
  // in a commit AFTER this component's first layout effect, so a plain ref is
  // still null when an effect keyed on `open` runs — the observer would never
  // attach and the previews would stay blank.
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);
  // Live blob: URL for the generated PNG — revoked on replace / close / unmount
  // (same pattern as PricePromoShareTab).
  const imageUrlRef = useRef<string | null>(null);

  const selectedTemplate = templates.find((t) => t.id === selectedId) ?? null;

  const clearImageUrl = useCallback(() => {
    if (imageUrlRef.current) {
      URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = null;
    }
    setImageUrl(null);
  }, []);

  const handleClosePreview = useCallback(() => {
    setSelectedId(null);
    clearImageUrl();
    setError(null);
    setGenerating(false);
  }, [clearImageUrl]);

  const handleClose = useCallback(() => {
    handleClosePreview();
    onClose();
  }, [handleClosePreview, onClose]);

  useEffect(() => () => clearImageUrl(), [clearImageUrl]);

  // Check whether the agent may register interest (has shared the Super
  // Connector design). Re-checked when the gallery opens and after each share.
  const checkInterestEligibility = useCallback(() => {
    api
      .getConnectorInterestEligibility()
      .then((r) => setInterestEligible(Boolean(r.eligible)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (open) checkInterestEligibility();
  }, [open, checkInterestEligibility]);

  // Track the grid width so each preview can scale to fill its cell. Measured
  // in a layout effect so the first paint already has the right scale.
  useLayoutEffect(() => {
    if (!gridEl) return;
    const columnCount = templates.length === 1 ? 1 : 2;
    const measure = () => setCellWidth(
      Math.max(0, (gridEl.clientWidth - GRID_GAP * (columnCount - 1)) / columnCount),
    );
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(gridEl);
    return () => ro.disconnect();
  }, [gridEl, templates.length]);

  // Render the selected design off-screen and rasterise it once its images
  // and the web fonts are ready.
  useEffect(() => {
    if (!selectedTemplate) return;
    let cancelled = false;

    setGenerating(true);
    clearImageUrl();
    setError(null);

    (async () => {
      try {
        const el = captureRef.current;
        if (!el) return;
        await waitForImages(el);
        if (document.fonts?.ready) {
          try {
            await document.fonts.ready;
          } catch {
            /* fonts.ready can reject on some browsers — capture anyway */
          }
        }
        // Give the browser one frame to settle layout before capture.
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        if (cancelled) return;

        const canvas = await html2canvas(el, {
          useCORS: true,
          scale: captureScale(),
          width: selectedTemplate.width,
          height: selectedTemplate.height,
          backgroundColor: null,
          logging: false,
        });
        if (cancelled) return;

        // Blob object URLs (same as Price Promo) — avoids huge base64 data URLs
        // that break iOS share/download.
        const url = await canvasToObjectUrl(canvas);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        imageUrlRef.current = url;
        setImageUrl(url);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to generate distinctive image:', err);
          setError('Could not generate the image. Please try again.');
        }
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Capture once per template selection. `values` is read from the closure
    // and is stable while this dialog is open, so it is intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, clearImageUrl]);

  const fileName = selectedTemplate ? `distinctive-${selectedTemplate.id}` : 'distinctive';

  // Record the share/download server-side (grants Price Promo access) and let
  // the parent refresh access. Fire-and-forget — never block the download/share.
  const recordShare = useCallback(() => {
    // A design may record a distinct share-template name (e.g. Super Connector
    // records "Super Connector Register Interest", which unlocks Register
    // Interest) while keeping its own gallery caption.
    const templateName = selectedTemplate?.shareTemplateName ?? selectedTemplate?.name;
    if (!templateName) return;
    api
      .recordDistinctiveShare(templateName)
      .then(() => {
        onShared?.();
        // Sharing the Super Connector design unlocks Register Interest.
        checkInterestEligibility();
      })
      .catch((err) => console.error('Failed to record distinctive share:', err));
  }, [selectedTemplate, onShared, checkInterestEligibility]);

  const downloadImage = useCallback(() => {
    if (!imageUrl) return;
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = `${fileName}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [imageUrl, fileName]);

  const handleDownload = useCallback(() => {
    downloadImage();
    recordShare();
  }, [downloadImage, recordShare]);

  const handleNativeShare = useCallback(async () => {
    if (!imageUrl) return;
    // Treat tapping Share the same as tapping Download: record the action
    // immediately so Register Interest unlocks without waiting for the native
    // share sheet to resolve (some mobile browsers keep that promise pending).
    recordShare();
    try {
      const blob = await (await fetch(imageUrl)).blob();
      const file = new File([blob], `${fileName}.png`, { type: 'image/png' });
      const nav = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
      };
      if (nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: 'Distinctives' });
      } else {
        downloadImage();
      }
    } catch (err) {
      // AbortError = user dismissed the share sheet; the Share click itself
      // has already been recorded, matching the Download-button behaviour.
      if ((err as Error)?.name !== 'AbortError') {
        console.error('Native share failed, falling back to download:', err);
        downloadImage();
      }
    }
  }, [imageUrl, fileName, downloadImage, recordShare]);

  const canNativeShare =
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { canShare?: unknown }).canShare === 'function';

  const previewFor = (template: DistinctiveTemplate) => {
    const scale = cellWidth > 0 ? cellWidth / template.width : 0;
    return (
      <Box key={template.id} sx={{ minWidth: 0 }}>
        <Box
          onClick={() => setSelectedId(template.id)}
          sx={{
            width: '100%',
            aspectRatio: `${template.width} / ${template.height}`,
            borderRadius: 2,
            overflow: 'hidden',
            cursor: 'pointer',
            border: '1px solid rgba(var(--color-text-rgb, 255,255,255), 0.15)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
            },
          }}
        >
          {scale > 0 && (
            <Box
              sx={{
                width: template.width,
                height: template.height,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                pointerEvents: 'none',
              }}
              dangerouslySetInnerHTML={{ __html: fillDistinctiveTemplate(template.markup, values) }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.75, textAlign: 'center', color: 'rgba(var(--color-text-rgb), 0.7)' }}
        >
          {template.name}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={templates.length === 1 ? 'sm' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              {description}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Box
          ref={setGridEl}
          sx={{
            display: 'grid',
            gridTemplateColumns: templates.length === 1 ? 'minmax(0, 1fr)' : 'repeat(2, 1fr)',
            gap: `${GRID_GAP}px`,
            alignItems: 'start',
            // A lone design is capped to the viewport height (less the dialog's
            // title, actions and caption) so its agent footer is never scrolled out of view.
            ...(templates.length === 1 && {
              maxWidth: `calc((100dvh - 300px) * ${templates[0].width / templates[0].height})`,
              mx: 'auto',
            }),
          }}
        >
          {templates.map(previewFor)}
        </Box>

        {/* Off-screen full-size render used as the html2canvas capture source. */}
        {selectedTemplate && (
          <Box
            aria-hidden
            sx={{
              position: 'fixed',
              top: 0,
              left: -100000,
              width: selectedTemplate.width,
              height: selectedTemplate.height,
              pointerEvents: 'none',
            }}
          >
            <div
              ref={captureRef}
              style={{ width: selectedTemplate.width, height: selectedTemplate.height }}
              dangerouslySetInnerHTML={{
                __html: fillDistinctiveTemplate(selectedTemplate.markup, values),
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
        }}
      >
        {hideInterestActions ? <span /> : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Tooltip
            title={interestEligible ? '' : 'Share or download the Super Connector design above to unlock'}
            disableHoverListener={interestEligible}
          >
            {/* span wrapper keeps the tooltip working while the button is disabled */}
            <span>
              <Button
                onClick={() => setInterestOpen(true)}
                disabled={!interestEligible || !user}
                disableElevation
                variant="contained"
                sx={{
                  textTransform: 'none',
                  pl: 1.75,
                  pr: 2.5,
                  py: 1,
                  minHeight: 52,
                  borderRadius: 2.5,
                  color: '#1a1206',
                  background: 'linear-gradient(135deg, #f6b24b 0%, #ed8936 55%, #e2622f 100%)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  boxShadow: '0 6px 16px rgba(237,137,54,0.5), inset 0 1px 0 rgba(255,255,255,0.4)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f7bd60 0%, #f0923f 55%, #e86a35 100%)',
                    boxShadow: '0 8px 22px rgba(237,137,54,0.65), inset 0 1px 0 rgba(255,255,255,0.5)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 3px 10px rgba(237,137,54,0.5)',
                  },
                  // Disabled still reads as a (locked) button: bordered orange-tint
                  // pill rather than near-invisible text.
                  '&.Mui-disabled': {
                    background: 'rgba(237,137,54,0.14)',
                    border: '1px solid rgba(237,137,54,0.45)',
                    color: 'rgba(237,137,54,0.85)',
                    boxShadow: 'none',
                  },
                }}
                startIcon={<SparkleIcon sx={{ fontSize: 22 }} />}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
                  <Box component="span" sx={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.2 }}>
                    Register Interest
                  </Box>
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, fontSize: 10.5, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.82 }}
                  >
                    Super Connector
                  </Box>
                </Box>
              </Button>
            </span>
          </Tooltip>
          {/* Secondary CTA: same pill silhouette as Register Interest, but an
              outlined orange-glass treatment so the gradient button stays the
              primary action. Gated exactly like Register Interest — share or
              download the design first. onClick (not href) so the link can't be
              followed while locked. */}
          <Tooltip
            title={interestEligible ? '' : 'Share or download the Super Connector design above to unlock'}
            disableHoverListener={interestEligible}
          >
            {/* span wrapper keeps the tooltip working while the button is disabled */}
            <span>
              <Button
                onClick={() => window.open('https://htns.sg/super-connector', '_blank', 'noopener,noreferrer')}
                disabled={!interestEligible || !user}
                disableElevation
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  pl: 1.75,
                  pr: 2.5,
                  py: 1,
                  minHeight: 52,
                  borderRadius: 2.5,
                  color: '#ed8936',
                  background: 'rgba(237,137,54,0.08)',
                  border: '1px solid rgba(237,137,54,0.55)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': {
                    background: 'rgba(237,137,54,0.16)',
                    border: '1px solid rgba(237,137,54,0.8)',
                    boxShadow: '0 6px 16px rgba(237,137,54,0.28), inset 0 1px 0 rgba(255,255,255,0.12)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
                  },
                  // Locked state mirrors Register Interest's: a dimmed orange-tint
                  // pill that still reads as a button.
                  '&.Mui-disabled': {
                    background: 'rgba(237,137,54,0.14)',
                    border: '1px solid rgba(237,137,54,0.45)',
                    color: 'rgba(237,137,54,0.85)',
                    boxShadow: 'none',
                  },
                }}
                startIcon={<PlayIcon sx={{ fontSize: 22 }} />}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
                  <Box component="span" sx={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.2 }}>
                    View Demo
                  </Box>
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, fontSize: 10.5, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.82 }}
                  >
                    Super Connector
                  </Box>
                </Box>
              </Button>
            </span>
          </Tooltip>
        </Box>
        )}
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>

      {/* Preview + download dialog */}
      <Dialog open={Boolean(selectedTemplate)} onClose={handleClosePreview} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">{selectedTemplate?.name}</Typography>
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: selectedTemplate
                ? `${selectedTemplate.width} / ${selectedTemplate.height}`
                : '1 / 1',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.2)',
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={selectedTemplate?.name}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : error ? (
              <Typography variant="body2" color="error" sx={{ p: 3, textAlign: 'center' }}>
                {error}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress />
                <Typography variant="caption" color="text.secondary">
                  Generating image…
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
          {canNativeShare && (
            <Button
              onClick={handleNativeShare}
              disabled={!imageUrl || generating}
              startIcon={<ShareIcon />}
            >
              Share
            </Button>
          )}
          <Button
            onClick={handleDownload}
            disabled={!imageUrl || generating}
            variant="contained"
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Super Connector — Register Interest (T&C + appointment booking) */}
      {user && (
        <SuperConnectorInterestDialog
          open={interestOpen}
          onClose={() => setInterestOpen(false)}
          user={user}
          onRegistered={() => {
            checkInterestEligibility();
            onRegistered?.();
          }}
        />
      )}
    </Dialog>
  );
};

export default DistinctiveDialog;
